import React from "react";
import { CircularProgress } from "@mui/material";
import { IconSparkles } from "@tabler/icons-react";
import CustomButton from "../utils/customButton";

interface SentimentAnalysisPanelProps {
  analyzing: boolean;
  sentimentData: any | null;
  onAnalyze: () => void;
}

const SentimentAnalysisPanel: React.FC<SentimentAnalysisPanelProps> = ({
  analyzing,
  sentimentData,
  onAnalyze,
}) => {
  return (
    <div className="bg-[#1F1F1F] p-6 rounded-lg">
      <h4 className="text-lg font-bold mb-4">Sentiment Analysis</h4>

      {analyzing ? (
        <div className="flex flex-col items-center justify-center py-4">
          <CircularProgress color="inherit" size={30} />
          <p className="mt-4 text-sm text-gray-400">
            Analyzing comments sentiment...
          </p>
        </div>
      ) : sentimentData ? (
        <div className="mb-4">
          {/* Sentiment Distribution */}
          <h3 className="text-base font-bold mb-2">Sentiment Distribution:</h3>
          <div className="flex w-full h-6 rounded-lg overflow-hidden mb-4">
            <div
              className="h-full bg-green-500"
              style={{
                width: `${sentimentData.percentages.positive * 100}%`,
              }}
            >
              <span className="text-white font-bold text-xs ml-2">
                Positive -{" "}
                {(sentimentData.percentages.positive * 100).toFixed(1)}%
              </span>
            </div>
            <div
              className="h-full bg-blue-500"
              style={{
                width: `${sentimentData.percentages.neutral * 100}%`,
              }}
            >
              <span className="text-white font-bold text-xs ml-2">
                Neutral - {(sentimentData.percentages.neutral * 100).toFixed(1)}
                %
              </span>
            </div>
            <div
              className="h-full bg-red-500"
              style={{
                width: `${sentimentData.percentages.negative * 100}%`,
              }}
            >
              <span className="text-white font-bold text-xs ml-2">
                Negative -{" "}
                {(sentimentData.percentages.negative * 100).toFixed(1)}%
              </span>
            </div>
          </div>

          {/* Main Sentiments */}
          <h3 className="text-base font-bold mb-2">Main Sentiments:</h3>
          <div className="flex justify-between mb-4 space-x-6">
            <div className="w-1/3 py-2">
              <ul className="space-y-1">
                {sentimentData.main_sentiments.positive.map(
                  (sentiment: string, index: number) => (
                    <li key={index}>
                      <div className="inline-block rounded-full bg-green-500/[.2] py-0.5 px-2.5 text-sm text-white">
                        {sentiment}
                      </div>
                    </li>
                  )
                )}
              </ul>
            </div>
            <div className="w-1/3 py-2">
              <ul className="space-y-1">
                {sentimentData.main_sentiments.neutral.map(
                  (sentiment: string, index: number) => (
                    <li key={index}>
                      <div className="inline-block rounded-full bg-blue-500/[.2] py-0.5 px-2.5 text-sm text-white">
                        {sentiment}
                      </div>
                    </li>
                  )
                )}
              </ul>
            </div>
            <div className="w-1/3 py-2">
              <ul className="space-y-1">
                {sentimentData.main_sentiments.negative.map(
                  (sentiment: string, index: number) => (
                    <li key={index}>
                      <div className="inline-block rounded-full bg-red-500/[.2] py-0.5 px-2.5 text-sm text-white">
                        {sentiment}
                      </div>
                    </li>
                  )
                )}
              </ul>
            </div>
          </div>
        </div>
      ) : (
        <>
          <p className="text-gray-400 mb-4">
            Analyze the sentiment of comments to understand how viewers feel
            about this video.
          </p>
          <CustomButton
            onClick={onAnalyze}
            endIcon={<IconSparkles size={16} />}
          >
            Run Sentiment Analysis
          </CustomButton>
        </>
      )}
    </div>
  );
};

export default SentimentAnalysisPanel;
